import { Button } from 'antd';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import { SelectedAssignment } from '../../../../pages/lms/assignment-mgmt/CreateAssignment';
import TextWinds from '../../../common/TextWinds';

interface SelectedAssignmentPanelProps {
  selectedAssignments: SelectedAssignment[];
  onRemove: (assignmentId: number) => void;
  onStudentSelect: (selectedAssignments: SelectedAssignment[]) => void;
}

/**
 * 개편 LMS (교사) - 과제 관리 > 과제 내주기 페이지 > 콘텐츠 영역 > 선택한 과제 판넬
 */
const SelectedAssignmentPanel = ({
  selectedAssignments,
  onRemove,
  onStudentSelect,
}: SelectedAssignmentPanelProps) => {
  const isDisabled = selectedAssignments.length === 0;

  return (
    <section className="flex h-[480px] flex-col rounded-xl bg-white p-4 shadow-card xl:h-[calc(100vh_-_180px)] xl:p-6">
      <div className="mb-5 flex shrink-0 items-start justify-between gap-4">
        <TextWinds type="title_h3">선택한 과제</TextWinds>

        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="primary"
            disabled={isDisabled}
            className={[
              'flex items-center gap-1.5 px-3 py-1.5',
              isDisabled
                ? '!cursor-not-allowed !border-gray3 !bg-gray1'
                : '!border-gray4 !bg-purple5 hover:!bg-purple4',
            ].join(' ')}
            onClick={() => onStudentSelect(selectedAssignments)}
          >
            <UserPlusIcon
              className={[
                'h-5 w-5',
                isDisabled ? 'text-gray5' : 'text-white',
              ].join(' ')}
            />

            <TextWinds type="caption1" color={isDisabled ? 'gray5' : 'white'}>
              학생 선택하기
            </TextWinds>
          </Button>

          <div className="rounded-full bg-gray1 px-3 py-1.5">
            <TextWinds type="caption1" color="gray8">
              총 {selectedAssignments.length}개
            </TextWinds>
          </div>
        </div>
      </div>

      {selectedAssignments.length === 0 ? (
        <div className="flex h-[200px] items-center justify-center rounded-xl border border-dashed border-gray4 bg-gray0">
          <TextWinds type="content_body2" color="gray6">
            선택한 과제가 없습니다.
          </TextWinds>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          <div className="flex flex-col gap-3">
            {selectedAssignments.map((assignment) => (
              <div
                key={assignment.id}
                className="flex items-start justify-between gap-3 rounded-xl border border-gray2 bg-white p-3 shadow-sm"
              >
                <div className="min-w-0">
                  <TextWinds type="content_body2" className="break-keep">
                    {assignment.title}
                  </TextWinds>

                  <TextWinds
                    type="caption1"
                    color="gray8"
                    className="mt-1 block break-keep"
                  >
                    {assignment.bookTitle}
                    {assignment.lessonTitle
                      ? ` · ${assignment.lessonTitle}`
                      : ''}
                  </TextWinds>
                </div>

                <button
                  type="button"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray7 hover:bg-gray1"
                  onClick={() => onRemove(assignment.id)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default SelectedAssignmentPanel;
